import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Warning, Sparkle, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useBranding } from "@/contexts/BrandingContext";
import { useI18n } from "@/lib/i18n";
import PriceEstimateNote from "@/components/PriceEstimateNote";
import { suggestVehicles } from "@/lib/vehicle";

const EMPTY = {
  client_name: "",
  client_phone: "",
  pickup_date: "",
  pickup_time: "",
  pickup_location: "",
  dropoff_location: "",
  passenger_count: 1,
  luggage_count: 0,
  vehicle_type: "eco",
  baby_seat: false,
  booster_seat: false,
  flight_number: "",
  train_number: "",
  notes: "",
  is_round_trip: false,
  return_date: "",
  return_time: "",
  return_pickup_location: "",
  return_dropoff_location: "",
};

export default function AgencyRequestForm({ open, onClose, onCreated }) {
  const branding = useBranding();
  const { t, lang } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const [estimate, setEstimate] = useState(null);

  // Live auto-estimate on pickup + dropoff + vehicle change.
  useEffect(() => {
    const p = (form.pickup_location || "").trim();
    const d = (form.dropoff_location || "").trim();
    if (!p || !d || form.vehicle_type === "moto") { setEstimate(null); return; }
    const ctrl = new AbortController();
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/pricing-match", {
          params: { from_location: p, to_location: d, vehicle_type: form.vehicle_type },
          signal: ctrl.signal,
        });
        if (data?.match?.default_price != null) {
          setEstimate({ price: data.match.default_price, source: data.source });
        } else { setEstimate(null); }
      } catch { /* silent */ }
    }, 450);
    return () => { clearTimeout(id); ctrl.abort(); };
  }, [form.pickup_location, form.dropoff_location, form.vehicle_type]);

  const upd = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const toggle = (field) => () => setForm((p) => ({ ...p, [field]: !p[field] }));

  const applyAiResult = (data) => {
    if (!data || typeof data !== "object") return;
    const normStr = (v) => (typeof v === "string" ? v : "");
    const normInt = (v, d = 0) => (Number.isFinite(+v) ? parseInt(v, 10) : d);
    const vt = data.vehicle_type === "van" ? "van" : "eco";
    setForm((p) => ({
      ...p,
      client_name: normStr(data.client_name) || p.client_name,
      client_phone: normStr(data.client_phone) || p.client_phone,
      pickup_date: normStr(data.pickup_date) || p.pickup_date,
      pickup_time: normStr(data.pickup_time) || p.pickup_time,
      pickup_location: normStr(data.pickup_location) || p.pickup_location,
      dropoff_location: normStr(data.dropoff_location) || p.dropoff_location,
      passenger_count: normInt(data.passenger_count, p.passenger_count) || 1,
      luggage_count: normInt(data.luggage_count, p.luggage_count) || 0,
      vehicle_type: vt,
      baby_seat: !!data.baby_seat || p.baby_seat,
      booster_seat: !!data.booster_seat || p.booster_seat,
      flight_number: normStr(data.flight_number) || p.flight_number,
      train_number: normStr(data.train_number) || p.train_number,
      notes: normStr(data.notes) || p.notes,
      is_round_trip: !!data.is_round_trip || p.is_round_trip,
      return_date: normStr(data.return_date) || p.return_date,
      return_time: normStr(data.return_time) || p.return_time,
    }));
    setAiApplied(true);
  };

  const runAi = async () => {
    if (!aiText.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ride-requests/ai-parse", { text: aiText });
      applyAiResult(data);
      toast.success("✓ " + t("ai.verify_notice"));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error(t("form.tc_link") + " ✱");
      return;
    }
    const required = ["client_name", "client_phone", "pickup_date", "pickup_time", "pickup_location", "dropoff_location"];
    for (const r of required) {
      if (!form[r]) { toast.error(t(`form.${r === "pickup_location" ? "pickup_loc" : r === "dropoff_location" ? "dropoff_loc" : r}`) || r); return; }
    }
    if (form.is_round_trip && (!form.return_date || !form.return_time)) {
      toast.error(t("form.return_date"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/ride-requests", {
        ...form,
        passenger_count: parseInt(form.passenger_count, 10) || 1,
        luggage_count: parseInt(form.luggage_count, 10) || 0,
        accepted_terms: true,
      });
      toast.success("✓");
      setForm(EMPTY);
      setAccepted(false);
      setAiText("");
      setAiApplied(false);
      onClose();
      // Defer parent-side work after the dialog close animation so we don't
      // stack two Radix dialogs at once (fixes mobile freeze / pointer-events).
      setTimeout(() => { onCreated?.(); }, 100);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="agency-form-title">{t("form.title")}</DialogTitle>
          <DialogDescription className="text-zinc-500">{branding.companyName}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="agency-request-form">

          {/* ── AI paste block ───────────── */}
          <div data-testid="arf-ai-block" className="rounded-md border border-amber-500/25 bg-amber-500/5 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-amber-400" />
              <p className="text-xs uppercase tracking-wider text-amber-300 font-medium">{t("ai.title")}</p>
            </div>
            <Textarea
              data-testid="arf-ai-text"
              rows={3}
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder={t("ai.placeholder")}
              className="bg-[#1A1A1A] border-zinc-700 text-white text-sm"
            />
            <Button
              data-testid="arf-ai-run"
              type="button"
              onClick={runAi}
              disabled={aiBusy || !aiText.trim()}
              className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-9 w-full"
            >
              {aiBusy ? t("ai.analyzing") : t("ai.cta")}
            </Button>
            {aiApplied && (
              <div data-testid="arf-ai-verify-notice" className="flex items-start gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-md p-2">
                <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
                <span>{t("ai.verify_notice")}</span>
              </div>
            )}
          </div>

          {/* ── Trip type ───────────── */}
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.trip_type")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="arf-trip-one-way"
                onClick={() => setForm((p) => ({ ...p, is_round_trip: false }))}
                className={`h-10 rounded-md border text-sm font-medium transition-colors ${!form.is_round_trip ? "bg-amber-500 text-black border-amber-500" : "bg-[#1A1A1A] text-zinc-300 border-zinc-700 hover:bg-zinc-800"}`}
              >{t("form.one_way")}</button>
              <button
                type="button"
                data-testid="arf-trip-round-trip"
                onClick={() => setForm((p) => ({ ...p, is_round_trip: true }))}
                className={`h-10 rounded-md border text-sm font-medium transition-colors ${form.is_round_trip ? "bg-amber-500 text-black border-amber-500" : "bg-[#1A1A1A] text-zinc-300 border-zinc-700 hover:bg-zinc-800"}`}
              >{t("form.round_trip")}</button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.client_name")}</Label>
              <Input data-testid="arf-client-name" required value={form.client_name} onChange={upd("client_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.client_phone")}</Label>
              <Input data-testid="arf-client-phone" required value={form.client_phone} onChange={upd("client_phone")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" placeholder="+33 ..." />
            </div>

            {form.is_round_trip && (
              <div className="col-span-2 -mb-1 text-[11px] text-amber-400 uppercase tracking-wider font-medium">{t("form.outbound")}</div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.pickup_date")}</Label>
              <Input data-testid="arf-pickup-date" type="date" required value={form.pickup_date} onChange={upd("pickup_date")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.pickup_time")}</Label>
              <Input data-testid="arf-pickup-time" type="time" required value={form.pickup_time} onChange={upd("pickup_time")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.pickup_loc")}</Label>
              <Input data-testid="arf-pickup-loc" required value={form.pickup_location} onChange={upd("pickup_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" placeholder="Paris 8 / CDG T2E / Hôtel..." />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.dropoff_loc")}</Label>
              <Input data-testid="arf-dropoff-loc" required value={form.dropoff_location} onChange={upd("dropoff_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>

            {estimate && (
              <div className="col-span-2">
                <PriceEstimateNote price={estimate.price} source={estimate.source} lang={lang} />
              </div>
            )}

            {/* Return leg */}
            {form.is_round_trip && (
              <>
                <div className="col-span-2 -mb-1 text-[11px] text-amber-400 uppercase tracking-wider font-medium mt-2">{t("form.return")}</div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.return_date")}</Label>
                  <Input data-testid="arf-return-date" type="date" required value={form.return_date} onChange={upd("return_date")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.return_time")}</Label>
                  <Input data-testid="arf-return-time" type="time" required value={form.return_time} onChange={upd("return_time")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.return_pickup")}</Label>
                  <Input data-testid="arf-return-pickup" value={form.return_pickup_location} onChange={upd("return_pickup_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" placeholder={form.dropoff_location || ""} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.return_dropoff")}</Label>
                  <Input data-testid="arf-return-dropoff" value={form.return_dropoff_location} onChange={upd("return_dropoff_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" placeholder={form.pickup_location || ""} />
                </div>
                <p className="col-span-2 text-[11px] text-zinc-500 -mt-1">{t("form.return_hint")}</p>
              </>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.passengers")}</Label>
              <Input data-testid="arf-passengers" type="number" min={1} value={form.passenger_count} onChange={upd("passenger_count")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.luggage")}</Label>
              <Input data-testid="arf-luggage" type="number" min={0} value={form.luggage_count} onChange={upd("luggage_count")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            {(() => {
              const p = parseInt(form.passenger_count || 0);
              if (!p || p < 5) return null;
              const s = suggestVehicles(form.passenger_count, form.luggage_count);
              return (
                <p data-testid="arf-vehicle-suggestion" className="col-span-2 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                  Suggestion : {s.label}{s.heavy ? " — bagages importants pris en compte." : ""}
                </p>
              );
            })()}
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.vehicle")}</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm((p) => ({ ...p, vehicle_type: v }))}>
                <SelectTrigger data-testid="arf-vehicle" className="bg-[#1A1A1A] border-zinc-700 text-white h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-zinc-700">
                  <SelectItem value="eco" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">{t("form.vehicle.eco")}</SelectItem>
                  <SelectItem value="van" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">{t("form.vehicle.van")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.child_eq")}</Label>
              <div className="flex gap-4 mt-1.5">
                <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                  <input data-testid="arf-baby-seat" type="checkbox" checked={form.baby_seat} onChange={toggle("baby_seat")} className="accent-amber-500" />
                  {t("form.child_eq_hint").includes("bébé") || t("form.child_eq_hint").includes("baby") || t("form.child_eq_hint").includes("bebé") ? "" : ""}
                  Baby seat
                </label>
                <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                  <input data-testid="arf-booster-seat" type="checkbox" checked={form.booster_seat} onChange={toggle("booster_seat")} className="accent-amber-500" />
                  Booster seat
                </label>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Flight #</Label>
              <Input data-testid="arf-flight" value={form.flight_number} onChange={upd("flight_number")} placeholder="AF1234" className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Train #</Label>
              <Input data-testid="arf-train" value={form.train_number} onChange={upd("train_number")} placeholder="TGV 8421" className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("form.notes")}</Label>
              <Textarea data-testid="arf-notes" value={form.notes} onChange={upd("notes")} rows={2} className="bg-[#1A1A1A] border-zinc-700 text-white" placeholder={t("form.notes_hint")} />
            </div>
          </div>

          <div data-testid="arf-capacity-warning" className="flex gap-2 p-3 rounded-md bg-amber-500/5 border border-amber-500/20 text-amber-200/80 text-xs leading-relaxed whitespace-pre-line">
            <Warning size={18} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
            <p>{t("form.capacity_warn")}</p>
          </div>

          <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
            <input
              data-testid="arf-accept-tc"
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 accent-amber-500"
              required
            />
            <span>
              {t("form.tc_text_1")}
              <a
                data-testid="arf-tc-link"
                href="https://sdrive-transfert.fr/legal-information/"
                target="_blank"
                rel="noopener noreferrer external"
                onClick={(e) => { e.stopPropagation(); }}
                className="text-amber-500 hover:text-amber-400 underline"
              >
                {t("form.tc_link")}
              </a>
              {t("form.tc_text_2")}
            </span>
          </label>

          <div className="flex gap-2 pt-2">
            <Button type="button" onClick={onClose} variant="outline" className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              {t("form.cancel")}
            </Button>
            <Button data-testid="arf-submit" type="submit" disabled={loading} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600">
              {loading ? "..." : t("form.submit")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
